/**
 * #C10: C7 Edit popup — management corrects the AI's read of the bridge letter:
 * does it cover the fiscal year-end, were there material changes, and a plain-
 * English summary. Mirrors the C1–C5 edit modal. Re-uploading the letter lives
 * on the card ("Replace bridge letter"); this is for fixing the verdict/summary.
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  type SelectProps,
} from "@cloudscape-design/components";
import type { BridgeEvidence } from "@/core/api/types";
import { updateBridgeEvidence } from "./api";

const COVERS: SelectProps.Option[] = [
  { label: "Yes — covers your fiscal year-end", value: "yes" },
  { label: "No — does not reach your year-end", value: "no" },
];
const MATERIAL: SelectProps.Option[] = [
  { label: "None — no material changes since period end", value: "no_changes" },
  { label: "Yes — material changes noted", value: "material_changes" },
];

export function BridgeEditModal({
  reportId,
  data,
  visible,
  onDismiss,
  onSaved,
}: {
  reportId: string;
  data: BridgeEvidence | null;
  visible: boolean;
  onDismiss: () => void;
  onSaved: () => void;
}) {
  const ai = data?.ai_data ?? null;
  const [covers, setCovers] = useState<string>(ai?.c21_covers_fye ?? "yes");
  const [material, setMaterial] = useState<string>(ai?.c22_material_changes ?? "no_changes");
  const [summary, setSummary] = useState(ai?.c21_rationale ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setCovers(ai?.c21_covers_fye ?? "yes");
      setMaterial(ai?.c22_material_changes ?? "no_changes");
      setSummary(ai?.c21_rationale ?? "");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, data?.id]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateBridgeEvidence(reportId, {
        covers_fye: covers,
        material_changes: material,
        summary,
      });
      onSaved();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Edit bridge-letter assessment"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={busy} loading={busy}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Save failed">
            {error}
          </Alert>
        )}
        <FormField label="Does the bridge letter cover your fiscal year-end?">
          <Select
            selectedOption={COVERS.find((o) => o.value === covers) ?? COVERS[0]!}
            onChange={({ detail }) => setCovers(detail.selectedOption.value ?? "yes")}
            options={COVERS}
          />
        </FormField>
        <FormField label="Material changes since the period end?">
          <Select
            selectedOption={MATERIAL.find((o) => o.value === material) ?? MATERIAL[0]!}
            onChange={({ detail }) => setMaterial(detail.selectedOption.value ?? "no_changes")}
            options={MATERIAL}
          />
        </FormField>
        <FormField label="Summary" description="Your assessment of the bridge letter, in plain English.">
          <Textarea value={summary} onChange={({ detail }) => setSummary(detail.value)} rows={5} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
